<template>
  <div ref="targetEl" :style="parentElementStyle">
    <slot v-if="shouldRender" />
  </div>
</template>

<script setup lang="ts">
// This component is based off the following article:
// https://medium.com/js-dojo/lazy-rendering-in-vue-to-improve-performance-dcccd445d5f
import { useIntersectionObserver } from "@vueuse/core";
import { computed, onUnmounted, ref, useTemplateRef } from "vue";

import type { LazyLoadOptions } from "@/models/common";
import type { PropType } from "vue";

const props = defineProps({
  options: {
    type: Object as PropType<LazyLoadOptions>,
  },
  // minHeight acts as a "placeholder" until child is rendered
  minHeight: {
    type: Number,
    // 40 just seemed ok as a default
    default: 40,
  },
  persist: {
    type: Boolean,
    default: false,
  },
});

const UNRENDER_DELAY = 15_000;
const ROOT_MARGIN_IN_PX = 500;

const shouldRender = ref(false);
const targetEl = useTemplateRef("targetEl");
const fixedMinHeight = ref(0);
const unrenderTimer = ref<number | undefined>(undefined);
const renderTimer = ref<number | undefined>(undefined);

const parentElementStyle = computed(
  () =>
    props.options?.style || {
      minHeight: (fixedMinHeight.value || props.minHeight) + "px",
      display: "flex",
      alignItems: "center",
    }
);

useIntersectionObserver(
  targetEl,
  ([{ isIntersecting }]) => {
    if (isIntersecting) {
      // Perhaps the user re-scrolled to a component that was set to unrender. In that case stop the unrendering timer
      clearTimeout(unrenderTimer.value);
      // If we're doing unrendering lets add a waiting period of 200ms before rendering.
      // If a component enters the viewport and also leaves it within 200ms it will not render at all.
      // This saves work and improves performance when user scrolls very fast
      renderTimer.value = window.setTimeout(() => {
        shouldRender.value = true;
        // Clear any previously set fixed width when re-entering viewport
        if (targetEl.value) {
          targetEl.value.style.width = "";
        }
      }, 200); // Increased from 100ms to 200ms to reduce rapid mount/unmount cycles
    } else {
      // If we want to render once and persist, return
      if (props.persist) {
        return;
      }
      // If the component was set to render, cancel that
      clearTimeout(renderTimer.value);
      unrenderTimer.value = window.setTimeout(() => {
        // Preserve the width when unmounting to prevent layout shift
        const children = Array.from(
          (targetEl.value?.children as
            | HTMLCollectionOf<HTMLElement>
            | undefined) || []
        );
        const childEl = children[0];
        if (targetEl.value && childEl) {
          targetEl.value.style.width = `${childEl.offsetWidth}px`;
        }
        fixedMinHeight.value = targetEl.value?.clientHeight || 0;
        shouldRender.value = false;
      }, UNRENDER_DELAY);
    }
  },
  {
    // https://vueuse.org/core/useintersectionobserver/#type-declarations
    rootMargin: `${ROOT_MARGIN_IN_PX}px `.repeat(4).trim(),
  }
);

onUnmounted(() => {
  clearTimeout(unrenderTimer.value);
  clearTimeout(renderTimer.value);
  shouldRender.value = false;
  fixedMinHeight.value = 0;
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.lazy-fade-enter-active,
.lazy-fade-leave-active {
  transition: opacity 0.5s ease;
}

.lazy-fade-enter-from,
.lazy-fade-leave-to {
  opacity: 0;
}
</style>
